/**
 * Emissions Settings Page
 *
 * Configure calculation methodology, reporting boundaries, and display settings.
 * Settings are stored at root organization only - subsidiaries inherit.
 */

import { useEffect, useState, useMemo, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ArrowLeft, FlaskConical, SlidersHorizontal, Info, Loader2 } from 'lucide-react';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { Dashboard } from '@g17eco/ui-modern/components/ui/dashboard';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@g17eco/ui-modern/components/ui/dialog';
import { generateUrl } from '@routes/util';
import { emissionsTrackerRoutes } from '../routes';
import { useGetSettingsQuery, useUpdateAllSettingsMutation, useRecalculateAllMutation } from '../api/emissions';
import type { GWPMethodology, EmissionsDisplayUnit, OrganizationalBoundary } from '../types/emissions';
import { REGION_OPTIONS } from '../components/form/types';
import { useAppSelector } from '@reducers/index';
import { isRootOrg } from '@selectors/initiative';

const selectClass = 'tw:flex tw:h-9 tw:w-full tw:rounded-md tw:border tw:border-input tw:bg-transparent tw:px-3 tw:py-1 tw:text-sm tw:shadow-sm tw:outline-none focus-visible:tw:ring-1 focus-visible:tw:ring-ring tw:cursor-pointer disabled:tw:cursor-not-allowed disabled:tw:opacity-50';

export const EmissionsSettingsPage = () => {
  const history = useHistory();
  const { initiativeId } = useParams<{ initiativeId: string }>();

  // Root organization check - settings can only be modified at root level
  const isRootOrganization = useAppSelector(isRootOrg);
  const isReadOnly = !isRootOrganization;

  const { data: settings, isLoading, error } = useGetSettingsQuery({ initiativeId });
  const [updateSettings, { isLoading: isSaving }] = useUpdateAllSettingsMutation();
  const [recalculateAll, { isLoading: isRecalculating }] = useRecalculateAllMutation();

  // Track original GWP to detect changes requiring recalculation
  const originalGwpRef = useRef<GWPMethodology | null>(null);
  const [showGwpRecalculateModal, setShowGwpRecalculateModal] = useState(false);

  // Form state
  const [gwpMethodology, setGwpMethodology] = useState<GWPMethodology>('AR5');
  const [defaultRegion, setDefaultRegion] = useState('GLOBAL');
  const [organizationalBoundary, setOrganizationalBoundary] = useState<OrganizationalBoundary>('operational_control');
  const [baseYear, setBaseYear] = useState(2024);
  const [displayUnit, setDisplayUnit] = useState<EmissionsDisplayUnit>('tCO2e');
  const [decimalPlaces, setDecimalPlaces] = useState(2);
  const [currency, setCurrency] = useState('USD');

  useEffect(() => {
    if (settings) {
      setGwpMethodology(settings.gwpMethodology);
      setDefaultRegion(settings.defaultRegion);
      setOrganizationalBoundary(settings.organizationalBoundary);
      setBaseYear(settings.baseYear);
      setDisplayUnit(settings.displayUnit);
      setDecimalPlaces(settings.decimalPlaces);
      setCurrency(settings.currency);
      // Store original GWP on first load
      if (originalGwpRef.current === null) {
        originalGwpRef.current = settings.gwpMethodology;
      }
    }
  }, [settings]);

  const isDirty = useMemo(() => {
    if (!settings) {
      return false;
    }
    return (
      gwpMethodology !== settings.gwpMethodology ||
      defaultRegion !== settings.defaultRegion ||
      organizationalBoundary !== settings.organizationalBoundary ||
      baseYear !== settings.baseYear ||
      displayUnit !== settings.displayUnit ||
      decimalPlaces !== settings.decimalPlaces ||
      currency !== settings.currency
    );
  }, [settings, gwpMethodology, defaultRegion, organizationalBoundary, baseYear, displayUnit, decimalPlaces, currency]);

  const handleBack = () => {
    history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_ADMIN, { initiativeId }));
  };

  const gwpChanged = originalGwpRef.current !== null && gwpMethodology !== originalGwpRef.current;

  const getSettingsPayload = () => ({
    initiativeId,
    data: {
      gwpMethodology,
      defaultRegion,
      organizationalBoundary,
      baseYear,
      displayUnit,
      decimalPlaces,
      currency,
    },
  });

  const handleSave = async () => {
    // Defensive check - server should also validate authorization
    if (isReadOnly) {
      return;
    }

    // If GWP changed, show confirmation modal for recalculation
    if (gwpChanged) {
      setShowGwpRecalculateModal(true);
      return;
    }

    try {
      await updateSettings(getSettingsPayload()).unwrap();
      originalGwpRef.current = gwpMethodology;
      generateToast({ color: 'success', message: 'Settings saved successfully' });
    } catch (error) {
      toastFromError({ error, title: 'Failed to save settings' });
    }
  };

  const handleGwpRecalculateConfirm = async () => {
    // Save settings first (idempotent - safe to retry)
    try {
      await updateSettings(getSettingsPayload()).unwrap();
    } catch (error) {
      toastFromError({ error, title: 'Failed to save settings' });
      return;
    }

    // Settings saved - update ref to reflect server state
    originalGwpRef.current = gwpMethodology;

    // Trigger recalculation
    try {
      const result = await recalculateAll({
        initiativeId,
        options: { gwpMethodology, recalculate: true },
      }).unwrap();

      setShowGwpRecalculateModal(false);
      generateToast({
        color: 'success',
        message: `Settings saved and ${result?.summary?.successful ?? 0} calculations recalculated using ${gwpMethodology} methodology`,
      });
    } catch (error) {
      // Keep modal open so user can retry recalculation
      toastFromError({ error, title: 'Settings saved but recalculation failed. Click the button to retry.' });
    }
  };

  const handleGwpRecalculateCancel = () => {
    if (isRecalculating) {
      return;
    }
    // Reset GWP to the server's current value to avoid orphaned state
    if (settings) {
      setGwpMethodology(settings.gwpMethodology);
      originalGwpRef.current = settings.gwpMethodology;
    }
    setShowGwpRecalculateModal(false);
  };

  if (isLoading) {
    return (
      <Dashboard>
        <div role='status' className='tw:flex tw:justify-center tw:items-center tw:py-12'>
          <Loader2 className='tw:size-6 tw:animate-spin tw:text-muted-foreground' />
        </div>
      </Dashboard>
    );
  }

  if (error) {
    return (
      <Dashboard>
        <div className='tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:p-4 tw:text-red-800 tw:mb-4 tw:text-sm'>
          Failed to load settings. Please try again.
        </div>
        <Button variant='outline' onClick={handleBack}>
          Go Back
        </Button>
      </Dashboard>
    );
  }

  return (
    <Dashboard>
      {/* Header */}
      <div className='tw:flex tw:items-center tw:gap-3 tw:mb-6'>
        <Button variant='ghost' size='icon' onClick={handleBack} className='tw:text-muted-foreground'>
          <ArrowLeft className='tw:size-4' />
        </Button>
        <div>
          <h4 className='tw:text-xl tw:font-semibold tw:mb-0'>Settings</h4>
          <p className='tw:text-sm tw:text-muted-foreground tw:mb-0'>Calculation, reporting, and display preferences</p>
        </div>
      </div>

      {/* Read-only notice for subsidiaries */}
      {isReadOnly && (
        <div className='tw:flex tw:gap-3 tw:rounded-lg tw:border tw:border-blue-200 tw:bg-blue-50 tw:p-4 tw:text-blue-800 tw:mb-6 tw:text-sm'>
          <Info className='tw:size-4 tw:shrink-0 tw:mt-0.5' />
          <div>
            <strong>Inherited Settings</strong>
            <p className='tw:mb-0 tw:mt-0.5'>
              Emission settings are managed at the root organization level and apply to all subsidiaries.
              Contact your organization administrator to request changes.
            </p>
          </div>
        </div>
      )}

      {/* GWP Methodology */}
      <Card className='tw:mb-3'>
        <CardContent className='tw:p-4'>
          <div className='tw:flex tw:justify-between tw:items-start tw:mb-3'>
            <div>
              <h5 className='tw:font-semibold tw:mb-1 tw:flex tw:items-center tw:gap-2'>
                <FlaskConical className='tw:size-4 tw:text-muted-foreground' />
                GWP Methodology
              </h5>
              <p className='tw:text-sm tw:text-muted-foreground tw:mb-0'>
                Global Warming Potential values determine how greenhouse gases are converted to CO2 equivalent
              </p>
            </div>
            <select
              value={gwpMethodology}
              onChange={(e) => setGwpMethodology(e.target.value as GWPMethodology)}
              className={cn(selectClass, 'tw:w-48')}
              disabled={isReadOnly}
            >
              <option value='AR4'>IPCC AR4 (2007)</option>
              <option value='AR5'>IPCC AR5 (2014)</option>
              <option value='AR6'>IPCC AR6 (2021)</option>
            </select>
          </div>
          <div className='tw:rounded-lg tw:bg-muted/50 tw:p-3 tw:mt-2'>
            <p className='tw:text-xs tw:text-muted-foreground tw:mb-2'>
              <strong>AR5</strong> (IPCC 2014) is recommended for GHG Protocol compliance and is widely accepted by reporting frameworks.
              <strong> AR6</strong> (IPCC 2021) uses updated science but may not yet be accepted by all frameworks.
              Changing methodology requires recalculating all historical emissions to ensure consistent reporting. You will be prompted to confirm before recalculation begins.
            </p>
            <table className='tw:w-full tw:text-xs'>
              <thead>
                <tr className='tw:border-b'>
                  <th className='tw:text-left tw:py-1.5 tw:font-medium'>Gas</th>
                  <th className='tw:text-left tw:py-1.5 tw:font-medium'>AR4</th>
                  <th className='tw:text-left tw:py-1.5 tw:font-medium'>AR5</th>
                  <th className='tw:text-left tw:py-1.5 tw:font-medium'>AR6</th>
                </tr>
              </thead>
              <tbody className='tw:divide-y'>
                <tr>
                  <td className='tw:py-1.5'>CO2</td>
                  <td className='tw:py-1.5'>1</td>
                  <td className='tw:py-1.5'>1</td>
                  <td className='tw:py-1.5'>1</td>
                </tr>
                <tr>
                  <td className='tw:py-1.5'>CH4 (Methane)</td>
                  <td className='tw:py-1.5'>25</td>
                  <td className='tw:py-1.5'>28</td>
                  <td className='tw:py-1.5'>27.9</td>
                </tr>
                <tr>
                  <td className='tw:py-1.5'>N2O (Nitrous Oxide)</td>
                  <td className='tw:py-1.5'>298</td>
                  <td className='tw:py-1.5'>265</td>
                  <td className='tw:py-1.5'>273</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Organizational Boundary */}
      <Card className='tw:mb-3'>
        <CardContent className='tw:p-4'>
          <div className='tw:flex tw:justify-between tw:items-start tw:mb-3'>
            <div>
              <h5 className='tw:font-semibold tw:mb-1'>Organizational Boundary</h5>
              <p className='tw:text-sm tw:text-muted-foreground tw:mb-0'>
                How emissions from subsidiaries and joint ventures are consolidated per GHG Protocol
              </p>
            </div>
            <select
              value={organizationalBoundary}
              onChange={(e) => setOrganizationalBoundary(e.target.value as OrganizationalBoundary)}
              className={cn(selectClass, 'tw:w-48')}
              disabled={isReadOnly}
            >
              <option value='operational_control'>Operational Control</option>
              <option value='financial_control'>Financial Control</option>
              <option value='equity_share' disabled>Equity Share (Coming Soon)</option>
            </select>
          </div>
          <div className='tw:rounded-lg tw:bg-muted/50 tw:p-3 tw:mt-2'>
            <ul className='tw:text-sm tw:text-muted-foreground tw:mb-0 tw:space-y-1.5 tw:list-disc tw:list-inside'>
              <li>
                <strong>Operational Control:</strong> 100% of emissions where you have authority over operating policies
              </li>
              <li>
                <strong>Financial Control:</strong> 100% of emissions where you direct financial and operating policies
              </li>
              <li>
                <strong>Equity Share:</strong> Emissions proportional to your equity ownership (coming soon)
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Base Year & Default Region */}
      <Card className='tw:mb-3'>
        <CardContent className='tw:p-4'>
          <div className='tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4'>
            <div>
              <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5'>Base Year</label>
              <select
                value={baseYear}
                onChange={(e) => setBaseYear(parseInt(e.target.value, 10))}
                className={cn(selectClass, 'tw:w-full')}
                disabled={isReadOnly}
              >
                <option value={2019}>2019</option>
                <option value={2020}>2020</option>
                <option value={2021}>2021</option>
                <option value={2022}>2022</option>
                <option value={2023}>2023</option>
                <option value={2024}>2024</option>
              </select>
              <p className='tw:text-xs tw:text-muted-foreground tw:mt-1'>Reference year for tracking emissions reduction progress</p>
            </div>
            <div>
              <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5'>Default Region</label>
              {/*
                * Region list comes from the canonical `REGION_OPTIONS`
                * derivation (`COUNTRY_CODES` + `SUPRANATIONAL_REGIONS`)
                * so this dropdown can never drift away from the
                * activity-wizard picker, the factor-search route's
                * z.enum, or the model's region constraint.
                */}
              <select
                value={defaultRegion}
                onChange={(e) => setDefaultRegion(e.target.value)}
                className={cn(selectClass, 'tw:w-full')}
                disabled={isReadOnly}
              >
                {REGION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <p className='tw:text-xs tw:text-muted-foreground tw:mt-1'>Pre-fills region when creating new activities</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Display Settings */}
      <Card className='tw:mb-6'>
        <CardContent className='tw:p-4'>
          <h5 className='tw:font-semibold tw:mb-1 tw:flex tw:items-center tw:gap-2'>
            <SlidersHorizontal className='tw:size-4 tw:text-muted-foreground' />
            Display Preferences
            <Badge variant='secondary' className='tw:text-xs tw:font-normal'>Coming Soon</Badge>
          </h5>
          <p className='tw:text-sm tw:text-muted-foreground tw:mb-3'>
            These settings will control how emissions are displayed throughout the application.
          </p>
          <div className='tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-4'>
            <div>
              <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5 tw:text-muted-foreground'>Emissions Unit</label>
              <select
                value={displayUnit}
                onChange={(e) => setDisplayUnit(e.target.value as EmissionsDisplayUnit)}
                className={cn(selectClass, 'tw:w-full')}
                disabled
              >
                <option value='kgCO2e'>kg CO2e</option>
                <option value='tCO2e'>Tonnes CO2e</option>
                <option value='short_ton'>US Short Tons</option>
              </select>
            </div>
            <div>
              <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5 tw:text-muted-foreground'>Decimal Places</label>
              <select
                value={decimalPlaces}
                onChange={(e) => setDecimalPlaces(parseInt(e.target.value, 10))}
                className={cn(selectClass, 'tw:w-full')}
                disabled
              >
                <option value={0}>0</option>
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
              </select>
            </div>
            <div>
              <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5 tw:text-muted-foreground'>Currency</label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className={cn(selectClass, 'tw:w-full')}
                disabled
              >
                <option value='USD'>USD</option>
                <option value='EUR'>EUR</option>
                <option value='GBP'>GBP</option>
                <option value='AED'>AED</option>
                <option value='AUD'>AUD</option>
                <option value='SGD'>SGD</option>
              </select>
              <p className='tw:text-xs tw:text-muted-foreground tw:mt-1'>For spend-based calculations</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className='tw:flex tw:justify-end tw:gap-2'>
        <Button variant='outline' onClick={handleBack}>
          Back
        </Button>
        {!isReadOnly && (
          <Button
            onClick={handleSave}
            disabled={!isDirty || isSaving || isRecalculating}
            className='tw:bg-emerald-800 tw:hover:bg-emerald-900 tw:text-white tw:border-0'
          >
            {isSaving || isRecalculating ? (
              <>
                <Loader2 className='tw:size-4 tw:animate-spin tw:mr-2' />
                {isRecalculating ? 'Recalculating...' : 'Saving...'}
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        )}
      </div>

      <Dialog open={showGwpRecalculateModal} onOpenChange={handleGwpRecalculateCancel}>
        <DialogContent showCloseButton={!isRecalculating} className='tw:sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>GWP Methodology Change</DialogTitle>
          </DialogHeader>
          <DialogDescription asChild className='tw:space-y-3 tw:text-foreground'>
            <div>
              <p>
                You are changing the GWP methodology from <strong>{originalGwpRef.current}</strong> to <strong>{gwpMethodology}</strong>.
              </p>
              <p>
                Per GHG Protocol guidelines, changing GWP methodology requires recalculating all historical emissions data
                to ensure accurate year-over-year comparisons.
              </p>
              <p className='tw:mb-0'>
                <strong>All existing calculations will be recalculated</strong> using the new methodology.
              </p>
            </div>
          </DialogDescription>
          <DialogFooter>
            <Button variant='outline' onClick={handleGwpRecalculateCancel} disabled={isRecalculating}>
              Cancel
            </Button>
            <Button onClick={handleGwpRecalculateConfirm} disabled={isRecalculating}>
              {isRecalculating ? (
                <>
                  <Loader2 className='tw:size-4 tw:animate-spin tw:mr-2' />
                  Recalculating...
                </>
              ) : (
                'Recalculate & Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dashboard>
  );
};
